import type { GraphicsSettings } from './graphics-settings';

type Policy = Pick<GraphicsSettings,'renderScale'|'minimumScale'|'targetFPS'|'frameLimit'>;
/** Frame pacing and slow, bounded resolution adaptation. No simulation clocks live here. */
export class FrameBudget {
  private policy: Policy = {renderScale:1,minimumScale:.5,targetFPS:0,frameLimit:0};
  private key = '';
  private lastRAF: number | null = null;
  private lastDraw: number | null = null;
  private deadline = 0;
  private calibration: number[] = [];
  private refreshHz: number | null = null;
  private ignoreUntil = 0;
  private samples: number[] = [];
  private sampleStart = 0;
  private stableSince = 0;
  private lastReduction = -Infinity;
  private hidden = false;
  private rendered = 0;
  private skipped = 0;
  lastFrameMS = 0;
  scale = 1;
  configure(settings: GraphicsSettings): void {
    const {renderScale,minimumScale,targetFPS,frameLimit}=settings, policy={renderScale,minimumScale,targetFPS,frameLimit}, key=JSON.stringify(policy);
    if(key===this.key) return;
    this.key=key;this.policy=policy;this.scale=renderScale;this.reset();
  }
  reset(): void {
    this.lastRAF=null;this.lastDraw=null;this.deadline=0;this.calibration=[];this.refreshHz=null;
    this.samples=[];this.sampleStart=0;this.stableSince=0;this.ignoreUntil=0;this.lastReduction=-Infinity;
  }
  suspendAdaptation(now: number, milliseconds=1000): void {
    this.ignoreUntil=Math.max(this.ignoreUntil,now+milliseconds);this.samples=[];this.sampleStart=0;this.stableSince=0;
  }
  private get target(): number {
    return this.policy.targetFPS ? Math.min(this.policy.targetFPS,this.policy.frameLimit||Infinity,this.refreshHz??Infinity) : 0;
  }
  /** Returns elapsed seconds for a drawn frame, or null when this RAF should not draw. */
  tick(now: number, visible=true, eligible=true): number | null {
    if(!visible) { if(!this.hidden) this.reset();this.hidden=true;return null; }
    this.hidden=false;
    const raf=this.lastRAF===null?0:now-this.lastRAF;this.lastRAF=now;
    if(raf>2000) { this.reset();this.lastRAF=now; }
    if(!eligible) this.suspendAdaptation(now);
    // Four light RAFs measure display cadence without assuming that a GPU-bound
    // scene's current FPS is the monitor's maximum. Networking continues normally.
    if(this.policy.targetFPS && this.refreshHz===null && eligible && now>=this.ignoreUntil && raf>0) {
      this.calibration.push(raf);
      if(this.calibration.length<4) {this.skipped++;return null;}
      const samples=this.calibration.slice(1).sort((a,b)=>a-b);
      this.refreshHz=Math.min(360,Math.max(15,1000/samples[1]!));
      this.suspendAdaptation(now);this.lastDraw=null;this.deadline=now;
    }
    const interval=this.policy.frameLimit?1000/this.policy.frameLimit:0;
    if(interval && this.lastDraw!==null && now+.5<this.deadline) {this.skipped++;return null;}
    const dt=this.lastDraw===null?0:Math.max(0,now-this.lastDraw);
    this.lastDraw=now;this.lastFrameMS=dt;this.rendered++;
    if(interval) {this.deadline+=interval;if(this.deadline<=now-interval||this.deadline===interval)this.deadline=now+interval;}
    if(this.target && this.refreshHz!==null && eligible && now>=this.ignoreUntil && dt>0) this.observe(now,dt);
    return Math.min(dt/1000,.1);
  }
  private observe(now: number,dt: number): void {
    if(!this.sampleStart) this.sampleStart=now;
    this.samples.push(dt);
    if(now-this.sampleStart<1000 || this.samples.length<4) return;
    const mean=this.samples.reduce((a,b)=>a+b,0)/this.samples.length, budget=1000/this.target;
    this.samples=[];this.sampleStart=now;
    if(mean>budget*1.12) {
      const step=mean>budget*1.5?.1:.05, floor=Math.min(this.policy.minimumScale,this.policy.renderScale);
      const next=Math.max(floor,Math.round((this.scale-step)*20)/20);
      if(next<this.scale) {this.scale=next;this.lastReduction=now;this.ignoreUntil=now+1000;}
      this.stableSince=0;
    } else if(mean<=budget*1.05) {
      this.stableSince ||= now;
      if(now-this.stableSince>=6000 && now-this.lastReduction>=12000 && this.scale<this.policy.renderScale) {
        this.scale=Math.min(this.policy.renderScale,Math.round((this.scale+.05)*20)/20);
        this.ignoreUntil=now+1000;this.stableSince=0;
      }
    } else this.stableSince=0;
  }
  get diagnostics() {return {frameLimit:this.policy.frameLimit,targetFPS:this.policy.targetFPS,effectiveTargetFPS:this.target,refreshHz:this.refreshHz,renderScale:this.scale,minimumScale:Math.min(this.policy.minimumScale,this.policy.renderScale),calibrating:!!this.policy.targetFPS&&this.refreshHz===null,rendered:this.rendered,skipped:this.skipped};}
}
